import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import {
  Target, Zap, BookOpen, Sparkles, Trophy, TrendingUp, MessageCircle, Rocket, Notebook, Loader2, User, Briefcase, CalendarClock,
} from "lucide-react";

const SECTIONS = [
  { key: "goal",         label: "Goals",                       icon: Target },
  { key: "profession",   label: "Profession",                  icon: Briefcase },
  { key: "strengths",    label: "Strengths",                   icon: Zap },
  { key: "weaknesses",   label: "Areas to improve",            icon: BookOpen },
  { key: "style",        label: "Learning style",              icon: Sparkles },
  { key: "achievements", label: "Achievements",                icon: Trophy },
  { key: "growth",       label: "Growth over time",            icon: TrendingUp },
  { key: "convos",       label: "Memorable conversations",     icon: MessageCircle },
  { key: "milestones",   label: "Recommended next milestones", icon: Rocket },
];

function formatWhen(ts) {
  try { return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

export default function NotebookPage() {
  const [nb, setNb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/notebook")
      .then((r) => setNb(r.data))
      .catch(() => setNb(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-24 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <div className="mt-3 label-mono">Your mentor is opening the notebook…</div>
      </div>
    );
  }
  if (!nb) return <div className="p-6 text-sm text-muted-foreground">Could not load your notebook.</div>;

  const goal = nb.goal || {};
  const profession = nb.profession || {};
  const planner = nb.study_planner || {};
  const mentor = nb.mentor || {};

  return (
    <div data-testid="notebook-root" className="space-y-8 animate-fade-up">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-2xl border border-primary/40 bg-primary/10 text-primary">
          <Notebook className="h-5 w-5" />
        </div>
        <div>
          <div className="label-mono text-primary">// mentor&apos;s notebook</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
            What {mentor.mentor_name || "Lane"} knows about you.
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            A living page — not a static profile. Every lesson you complete, quiz you take, and question you ask updates what your mentor understands about your journey.
          </p>
        </div>
      </div>

      {/* Snapshot */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-5">
          <div className="label-mono">Level & XP</div>
          <div className="mt-2 flex items-end gap-2">
            <span className="font-display text-3xl font-black">L{nb.user?.level ?? 1}</span>
            <span className="text-muted-foreground pb-1">· {nb.user?.xp ?? 0} XP</span>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono">Streak</div>
          <div className="mt-2 font-display text-3xl font-black">{nb.user?.streak ?? 0} <span className="text-sm text-muted-foreground">days</span></div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono">Joined</div>
          <div className="mt-2 font-display text-lg">{formatWhen(nb.user?.joined)}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono">Mentor</div>
          <div className="mt-2 font-display text-lg">{mentor.mentor_name || "Lane"}</div>
          <div className="label-mono">{mentor.personality || "Friendly Coach"} · {mentor.pace || "Balanced"}</div>
        </div>
      </div>

      {/* Section: Goal + Profession + Planner */}
      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <div className="flex items-center gap-2 label-mono text-primary"><Target className="h-3.5 w-3.5" /> goal</div>
          <div className="mt-2 font-display text-lg font-bold leading-snug">{goal.target || "not set"}</div>
          {goal.kind && <div className="mt-1 label-mono">category · {goal.kind}</div>}
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 label-mono"><Briefcase className="h-3.5 w-3.5" /> profession</div>
          <div className="mt-2 font-display text-lg font-bold">{profession.industry || "—"}</div>
          {profession.role && <div className="label-mono">{profession.role}</div>}
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 label-mono"><CalendarClock className="h-3.5 w-3.5" /> planner</div>
          {planner.days?.length ? (
            <>
              <div className="mt-2 font-display text-base font-bold">
                {planner.duration_minutes} min · {planner.preferred_time}
              </div>
              <div className="label-mono">{planner.days.length} days/wk · {planner.intensity}</div>
            </>
          ) : (
            <div className="mt-2 label-mono">not set · <Link to="/app/planner" className="text-primary hover:underline">set it now</Link></div>
          )}
        </div>
      </section>

      {/* Learning style */}
      {nb.learning_style_note && (
        <section className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
          <div className="flex items-center gap-2 label-mono text-accent"><Sparkles className="h-3.5 w-3.5" /> how you learn best</div>
          <p className="mt-2 text-sm leading-relaxed">{nb.learning_style_note}</p>
        </section>
      )}

      {/* Strengths + Weaknesses side by side */}
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 label-mono text-accent"><Zap className="h-3.5 w-3.5" /> strengths</div>
          {nb.strengths?.length ? (
            <ul className="mt-3 flex flex-wrap gap-2">
              {nb.strengths.map((s, i) => (
                <li key={i} className="rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs">{s}</li>
              ))}
            </ul>
          ) : <div className="mt-2 label-mono">nothing recorded yet — complete a quiz to seed this list</div>}
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 label-mono text-primary"><BookOpen className="h-3.5 w-3.5" /> areas to improve</div>
          {nb.weaknesses?.length ? (
            <ul className="mt-3 flex flex-wrap gap-2">
              {nb.weaknesses.map((s, i) => (
                <li key={i} className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs">{s}</li>
              ))}
            </ul>
          ) : <div className="mt-2 label-mono">no weak spots identified yet — you're doing great</div>}
        </div>
      </section>

      {/* Achievements */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 label-mono"><Trophy className="h-3.5 w-3.5 text-accent" /> achievements</div>
        {nb.achievements?.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {nb.achievements.map((b) => (
              <span key={b} className="rounded-full border border-accent/40 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">🏆 {b.replace(/_/g, " ")}</span>
            ))}
          </div>
        ) : <div className="mt-2 label-mono">complete a first lesson to unlock your first badge</div>}
      </section>

      {/* Growth timeline */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 label-mono"><TrendingUp className="h-3.5 w-3.5 text-primary" /> growth over time</div>
        {nb.growth_timeline?.length ? (
          <ol className="mt-3 space-y-2 relative border-l border-border pl-4">
            {nb.growth_timeline.map((g, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                <div className="text-sm">{g.summary}</div>
                <div className="label-mono">{g.kind} · {new Date(g.ts).toLocaleString()}</div>
              </li>
            ))}
          </ol>
        ) : <div className="mt-2 label-mono">your journey timeline will start filling up once you complete lessons</div>}
      </section>

      {/* Memorable conversations */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 label-mono"><MessageCircle className="h-3.5 w-3.5 text-primary" /> memorable conversations</div>
        {nb.memorable_conversations?.length ? (
          <ul className="mt-3 space-y-2">
            {nb.memorable_conversations.map((c) => (
              <li key={c.session_id}>
                <Link to={`/app/tutor/${c.session_id}`} className="block rounded-lg border border-border p-3 hover:border-primary/50 transition">
                  <div className="font-medium text-sm truncate">{c.title}</div>
                  {c.preview && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">"{c.preview}"</div>}
                  <div className="mt-1 label-mono">{formatWhen(c.when)}</div>
                </Link>
              </li>
            ))}
          </ul>
        ) : <div className="mt-2 label-mono">talk with your mentor and I&apos;ll remember what we discussed</div>}
      </section>

      {/* Milestones */}
      <section className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <div className="flex items-center gap-2 label-mono text-primary"><Rocket className="h-3.5 w-3.5" /> recommended next milestones</div>
        {nb.next_milestones?.length ? (
          <ol className="mt-3 space-y-2 list-decimal pl-5">
            {nb.next_milestones.map((m, i) => (
              <li key={i} className="text-sm leading-relaxed">{m}</li>
            ))}
          </ol>
        ) : <div className="mt-2 label-mono">milestones will appear once you have a goal and some progress</div>}
      </section>

      <div className="text-center label-mono pt-4">
        Notebook regenerates on every visit — everything above evolves as you learn.
      </div>
    </div>
  );
}
